import { Card, CardContent } from '@/components/ui/card'
import { RowWithSeparator } from '@/components/row-with-separator'
import { SalaryType } from '@/lib/generated/graphql-types'
import { Badge } from '@/components/ui/badge'
import { SalaryCompDescription } from '@/modules/salary/common/salary-comp-description'
import { AcademicBadge } from '@/modules/salary/common/accademic-badge'
import { PLACEHOLDER_SALARY } from '../constants'
import { getJobTypeTaxStatus } from '@/modules/salary/common/salary-details-utils'
import {
  formatEmployerTypeDetails,
  formatYearsOfExperience,
  getEstimatedFte,
  getTimeAgo,
  getTotalCompensation,
} from '@/modules/salary/common/utils'
import { SalaryInteractionIndicator } from './salary-interation-indicator'
import { SalaryEditButton } from '@/modules/user/profile/salaries/salary-edit-button'

export function SalaryMobileRow({
  salary,
  blurTotalCompensation,
  onRowClick,
  index,
  isHourlyPayPeriod,
  isLocumsOrPrn,
  className,
  hideInteractionIndicator,
  onEditButtonClick,
  isEditButtonLoading,
}: {
  salary: SalaryType
  blurTotalCompensation?: boolean
  onRowClick?: (salary: SalaryType) => void
  index?: number
  isHourlyPayPeriod?: boolean
  isLocumsOrPrn?: boolean
  className?: string
  hideInteractionIndicator?: boolean
  onEditButtonClick?: () => void
  isEditButtonLoading?: boolean
}) {
  return (
    <Card
      className={className}
      data-testid={`mobile-salary-row-${index}`}
      {...(onRowClick
        ? {
            onClick: () => {
              onRowClick(salary)
            },
            role: 'button',
          }
        : {})}
    >
      <CardContent className="relative px-4 py-3">
        <div className="flex justify-between items-center">
          <p className="text-c-18 font-bold text-green-40">
            {blurTotalCompensation ? (
              <span className="blur-xs">{PLACEHOLDER_SALARY}</span>
            ) : (
              <span>
                {getTotalCompensation(salary, { isHourly: isHourlyPayPeriod })}
              </span>
            )}
          </p>
          <span className="text-c-14 text-copy-muted flex items-center gap-2">
            {getTimeAgo(salary.postedAt)}
          </span>
        </div>
        <p className="text-copy-muted text-c-14">
          <SalaryCompDescription
            salary={salary}
            amountClassName={blurTotalCompensation ? 'blur-xs' : ''}
          />
        </p>
        <div className="font-semibold text-copy-primary text-sm mt-1.5 flex items-center gap-2">
          <span className="truncate">
            {salary.occupation?.baseTitlePlusSpecialty}
          </span>
          {salary.submittedByCurrentUser ? (
            <Badge variant="yellow">You</Badge>
          ) : null}
        </div>
        <RowWithSeparator
          elements={[
            salary.displayLocation,
            formatYearsOfExperience(salary.yearsOfExperience),
          ]}
          classNameElements="text-copy-primary text-c-14"
        />
        <div className="items-center text-c-14">
          <span className="text-copy-muted">
            {formatEmployerTypeDetails(
              salary.employerType,
              salary.employerTypeDetails,
              salary.practiceSetting,
              isLocumsOrPrn,
            )}
          </span>
          {salary.academicAffiliation && !isLocumsOrPrn ? (
            <AcademicBadge academicAffiliation={salary.academicAffiliation} />
          ) : null}
        </div>
        <RowWithSeparator
          elements={[
            getJobTypeTaxStatus(salary.jobType, salary.taxStatus),
            getEstimatedFte(salary.estimatedFte),
            `${salary.avgHoursWorked} hrs/week`,
          ]}
          classNameElements="text-copy-muted text-c-14"
        />
        {/* using -4 to offset the height of the interaction indicator */}
        <div className="absolute right-4 top-1/2 translate-y-[-4]">
          <SalaryInteractionIndicator show={!hideInteractionIndicator} />
        </div>
        {onEditButtonClick ? (
          <SalaryEditButton
            onEditButtonClick={onEditButtonClick}
            isEditButtonLoading={isEditButtonLoading ?? false}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}
